import emptyStateTexture from '@/assets/empty-state-texture.svg';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import { TypeWriterText } from '@/components/feedback/typewriter-text';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useApprovedResources } from '@/features/copilot/tools/approval';
import { ThinkingIcon } from '@/features/copilot/components/thinking-icon';
import { buildAiFrom } from '@/features/copilot/utils/build-ai-from';
import { close, Sidebar, SidebarHeader, toggle } from '@/features/sidebar';
import { useCopilotSidebar } from './hooks/use-copilot-sidebar';
import { useSupportChat } from '@/features/support';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useNetworkStatus } from '@/hooks/browser/use-network-status';
import { useAnalytics } from '@/services/analytics';
import { useServicesMutation } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, hasFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { AiEnabledGuard, AiNotEnabledFallback } from '@/components/guards';
import { generatePath } from '@/services/router/generate-path';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { copyToClipboard } from '@/utils/clipboard';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import {
  SUPPORTED_FILE_TYPES,
  type CopilotMessage,
  type CopilotModelMetaMessage,
  type CopilotSession,
  type CopilotStatefulMessage,
  type CopilotStatefulToolCall,
  type CopilotToolCallStatus,
  type CopilotUploadFile,
} from '@customerio/hydra-copilot';
import {
  useCopilotApproval,
  useCopilotAvailableTools,
  useCopilotFileUpload,
  useCopilotMessages,
  useCopilotMeta,
  useCopilotQueuedMessages,
  useCopilotStatus,
  useCopilotWebSearch,
} from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  Box,
  Button,
  CodeBlock,
  Divider,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Form,
  FormLayout,
  Heading,
  Icon,
  Image,
  InlineStack,
  KeyboardShortcut,
  Link,
  Paragraph,
  PlainButton,
  Popover,
  PopoverContent,
  Scrollable,
  SkeletonShape,
  SkeletonText,
  Stack,
  Strong,
  Text,
  TextArea,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import * as chatStyles from './chat.css';
import { AiGradientSparkleIcon } from './components/ai-gradient-sparkle-icon';
import { Attachments, type FileData } from './components/attachments';
import { Conversation, ConversationScrollButton, type ConversationRef } from './components/conversation';
import { FileUploadArea, type FileUploadAreaRef } from './components/file-upload-area';
import { Markdown } from './components/primitives/markdown';
import { MessageAction, MessageActionsContainer, MessageContainer } from './components/primitives/message-container';
import { extractFilename, uploadFilesWithRetry } from './helpers/file-utils';

import { AgentBetaLabel } from './components/agent-beta-label';
import {
  BashDetail,
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSource,
  ChainOfThoughtSources,
  ChainOfThoughtStep,
  type StepStatus,
} from './components/primitives/chain-of-thought';
import { References, type Reference } from './components/references';
import { BusinessProfileUsage } from './components/references/business-profile-usage';
import { useCopilotChatContext, useCopilotSessionContext, useMasqAccessLevel } from './components/session-provider';
import type { SuggestionState } from './components/suggestions';
import { MarqueeSuggestions, Suggestions, useSuggestions } from './components/suggestions';
import { Tour } from './components/tour';
import { dedupeReferences } from './utils/references';
import { getToolCallHeaderLabel, getToolCallLabel } from './utils/tool-call-labels';

import { useCustomerFeedback } from '@/features/customer-feedback';
import { useGreeting } from '@/features/greeting';
import {
  ENTRANCE_FADE_UP_ANIMATE,
  entranceExpressiveTransition,
  entranceFadeUpInitial,
  entranceStaggerTransition,
} from '@/utils/motion';
import { useMatches } from 'react-router-dom';
import { useIsAgentFullscreen } from './hooks';
import * as styles from './index.css';

/** Wraps the copilot welcome block in one entrance; keeps heading/description/actions in DOM order for a11y. */
const MotionWelcomePanel = motion.create(Box);
/** Sidebar column slot that must flex-grow; plain motion.div does not receive Pluma flex props. */
const MotionSidebarMessagesColumn = motion.create(Box);

export function CopilotActions() {
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  if (workspace == null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton isIconOnly icon="more" variant="subtle" size="sm" />
      <DropdownMenuContent>
        <DropdownMenuItem
          icon="add"
          onClick={() => {
            session.reset();
            track('aiAssistant', 'newChatClicked', {});
          }}
        >
          New chat
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="time-window"
          onClick={() => {
            toggle('copilot-history');
          }}
        >
          History
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="sparkle"
          href={generatePath('/workspaces/:workspaceId/settings/ai-business-profile', { workspaceId: workspace.id })}
        >
          Context
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="trigger-date"
          href={generatePath('/workspaces/:workspaceId/agent/routines', { workspaceId: workspace.id })}
        >
          Routines
        </DropdownMenuItem>
        <DropdownMenuItem icon="manage" href="/settings/privacy">
          AI settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CopilotName() {
  const session = useCopilotSessionContext();
  const meta = useCopilotMeta(session);
  const isV2 = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const defaultName = isV2 ? 'Agent' : 'Assistant';

  return (
    <Truncated>
      <TypeWriterText text={meta.name ?? defaultName} />
    </Truncated>
  );
}

export function CopilotInput({
  onSubmit,
  placeholder,
  wrapperClassName,
  newSessionOnSubmit = false,
}: {
  onSubmit?: () => void;
  placeholder?: string;
  wrapperClassName?: string;
  newSessionOnSubmit?: boolean;
} = {}) {
  const { session, input: sharedInput, setInput: setSharedInput } = useCopilotChatContext();
  const [localInput, setLocalInput] = useState('');

  const input = newSessionOnSubmit ? localInput : sharedInput;
  const setInput = newSessionOnSubmit ? setLocalInput : setSharedInput;

  const resetSession = useCallback(() => session.reset(), [session]);

  return (
    <Input
      session={session}
      input={input}
      setInput={setInput}
      onSubmit={onSubmit}
      placeholder={placeholder}
      wrapperClassName={wrapperClassName}
      resetSession={newSessionOnSubmit ? resetSession : undefined}
    />
  );
}

export function CopilotSupportButton() {
  const supportChat = useSupportChat();

  if (!supportChat.isVisible || !supportChat.isHandoverAllowed) {
    return null;
  }

  return (
    <Tooltip content="Chat with support" isCustom={true}>
      {(context) => (
        <Button
          variant="subtle"
          onClick={async () => {
            const success = await supportChat.openChat();
            if (success) {
              close();
            }
          }}
          isLoading={supportChat.isLoading}
          size="sm"
          isIconOnly={true}
          icon="chat-question"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
    </Tooltip>
  );
}

export function CopilotMessages() {
  const session = useCopilotSessionContext();
  const messages = useCopilotMessages(session);
  const status = useCopilotStatus(session);
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const { isMasqWithoutAccess, isMasqWithLimitedAccess } = useMasqAccessLevel();
  const isSending = status === 'sending';
  const isReceiving = status === 'receiving';
  const shouldReduceMotion = useReducedMotion();

  const [copilotWelcomeCycle, setCopilotWelcomeCycle] = useState(0);
  const previousSessionForWelcomeRef = useRef(session);
  // Not useMountEffect: replay welcome entrance when the session instance changes (e.g. New chat).
  useEffect(() => {
    if (previousSessionForWelcomeRef.current !== session) {
      previousSessionForWelcomeRef.current = session;
      setCopilotWelcomeCycle((n) => n + 1);
    }
  }, [session]);

  const welcomeMotionInitial = entranceFadeUpInitial(shouldReduceMotion);

  const supportChat = useSupportChat();
  const { track } = useAnalytics();
  const suggestionState = useSuggestions(session);
  const greeting = useGreeting({ withEmoji: false });
  const conversationRef = useRef<ConversationRef>(null);

  useEffect(() => {
    if (suggestionState.isVisible === true) {
      conversationRef.current?.scrollToBottom();
    }
  }, [suggestionState.isVisible]);

  const handleSuggestionClick = async (message: string, index: number) => {
    try {
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'sidebar',
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <Box flexGrow="1" h="0" display="flex" flexDirection="column">
      {isMasqWithLimitedAccess ? (
        <Box px="200" pt="100" flexShrink="0">
          <Banner variant="warning" title="Masquerade session">
            <Text>
              {isMasqWithoutAccess
                ? "You don't have masquerade access to this account. The agent cannot read or modify account data."
                : 'You are masquerading into this account. The agent may not be able to access account data.'}
            </Text>
          </Banner>
        </Box>
      ) : null}
      {status === 'error' ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="error">Failed to load</EmptyStateTitle>
            <EmptyStateDescription>
              There was an error loading {isV2 === true ? 'Agent' : 'Assistant'}. Please try again.
            </EmptyStateDescription>
            <EmptyStateActions>
              <Button
                variant="primary"
                onClick={() => {
                  session.reset();
                }}
              >
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyState>
        </Box>
      ) : (
        <>
          {status === 'initializing' ? (
            <Box flexGrow="1" display="flex" flexDirection="column" className={chatStyles.scrollContentWrapper}>
              <ChatSkeleton />
            </Box>
          ) : messages.length === 0 ? (
            <Box
              flexGrow="1"
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
              pb="800"
              px="200"
            >
              <MotionWelcomePanel
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="150"
                w="full"
                initial={welcomeMotionInitial}
                animate={ENTRANCE_FADE_UP_ANIMATE}
                transition={entranceExpressiveTransition(shouldReduceMotion)}
                key={`copilot-welcome-${copilotWelcomeCycle}`}
              >
                <AiGradientSparkleIcon size={40} />
                <Heading level="1" textAlign="center">
                  {greeting.hello}
                </Heading>
                <Text color="subtle" textAlign="center">
                  {greeting.tagline}
                </Text>
                <Box pt="200" position="relative" style={{ maxWidth: '360px', overflow: 'hidden' }}>
                  <Image
                    src={emptyStateTexture}
                    position="absolute"
                    m="auto"
                    style={{ inset: 0, opacity: 0.25, pointerEvents: 'none' }}
                  />
                  <MarqueeSuggestions
                    onSuggestionClick={handleSuggestionClick}
                    shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
                  />
                </Box>
              </MotionWelcomePanel>
            </Box>
          ) : (
            <>
              <Conversation ref={conversationRef} footer={<ConversationScrollButton />}>
                <Box display="flex" flexDirection="column" gap="150" className={chatStyles.scrollContentWrapper}>
                  {messages.map((message, index) => (
                    <Message
                      key={index}
                      message={message}
                      session={session}
                      isLastMessage={!isSending && index === messages.length - 1}
                      isStreaming={isReceiving && index === messages.length - 1}
                    />
                  ))}
                  {isSending || isReceiving ? (
                    <AnimateMessage>
                      <Box py="100">
                        <CyclingStatusText />
                      </Box>
                    </AnimateMessage>
                  ) : null}
                </Box>
              </Conversation>
              <Box className={chatStyles.scrollContentWrapper} flexShrink="0">
                <NextMessageSuggestions
                  state={suggestionState}
                  onSuggestionClick={handleSuggestionClick}
                  shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
                />
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}

function AiAgentTitle() {
  return <>AI Agent</>;
}

export function SidebarChat() {
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const { handleToggle } = useCopilotSidebar();
  const safeNavigate = useSafeNavigate();
  const isAgentFullscreen = useIsAgentFullscreen();
  const isAiAssistantV2Enabled = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  if (workspace == null) {
    return null;
  }

  const sidebarPanelInitial = entranceFadeUpInitial(shouldReduceMotion);

  return (
    <Sidebar
      ref={sidebarRef}
      id="copilot"
      onToggle={handleToggle}
      onToggleComplete={(isOpen) => {
        if (isOpen) {
          sidebarRef.current?.querySelector<HTMLTextAreaElement>('#copilot-chat-input')?.focus();
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <motion.div
          initial={sidebarPanelInitial}
          animate={ENTRANCE_FADE_UP_ANIMATE}
          transition={entranceStaggerTransition(0, shouldReduceMotion)}
        >
          <SidebarHeader
            title={
              <AiEnabledGuard fallback={AiAgentTitle}>
                <CopilotName />
              </AiEnabledGuard>
            }
            titleSuffix={
              <AiEnabledGuard>
                <AgentBetaLabel />
              </AiEnabledGuard>
            }
            leadingActions={
              <AiEnabledGuard>
                <CopilotActions />
              </AiEnabledGuard>
            }
            trailingActions={
              <AiEnabledGuard>
                <CopilotSupportButton />

                {isAiAssistantV2Enabled && !isAgentFullscreen ? (
                  <Tooltip content="Open full screen" isCustom={true}>
                    {(context) => (
                      <Button
                        variant="subtle"
                        size="sm"
                        icon="expand"
                        isIconOnly={true}
                        onClick={() => {
                          if (workspace != null) {
                            track('aiAssistant', 'expandedToFullScreen', {});
                            safeNavigate(
                              generatePath(
                                '/workspaces/:workspaceId/agent',
                                { workspaceId: workspace.id },
                                { 'ai-from': buildAiFrom() },
                              ),
                            );
                          }
                        }}
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                      />
                    )}
                  </Tooltip>
                ) : null}
              </AiEnabledGuard>
            }
            onClose={() => {
              toggle('copilot');
            }}
          />
        </motion.div>
        <AiEnabledGuard fallback={AiNotEnabledFallback}>
          <motion.div
            initial={sidebarPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(1, shouldReduceMotion)}
          >
            <Tour />
          </motion.div>
          <MotionSidebarMessagesColumn
            display="flex"
            flexDirection="column"
            flexGrow="1"
            minH="0"
            w="full"
            initial={sidebarPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(2, shouldReduceMotion)}
          >
            <CopilotMessages />
          </MotionSidebarMessagesColumn>
          <motion.div
            initial={sidebarPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(3, shouldReduceMotion)}
          >
            <Box p="100">
              <CopilotInput />
            </Box>
          </motion.div>
        </AiEnabledGuard>
      </Box>
    </Sidebar>
  );
}

function Message({
  message,
  session,
  isLastMessage = false,
  isStreaming = false,
}: {
  message: CopilotStatefulMessage;
  session: CopilotSession;
  isLastMessage?: boolean;
  isStreaming?: boolean;
}) {
  const auth = useAuth();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const [feedbackReasonOpen, setFeedbackReasonOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');

  const submitFeedbackMutation = useServicesMutation('recordCopilotFeedback', {
    onSuccess: (_, variables) => {
      snackbars.create({
        message: 'Thank you for your feedback!',
      });

      if (!('meta' in message) || message.meta == null || (message.meta as any).id == null) {
        return;
      }

      session.setMessageMeta((message.meta as any).id, {
        ...message.meta,
        thumbs_up: variables.body?.thumbs_up,
        thumbs_down: variables.body?.thumbs_down,
      });

      setFeedbackReasonOpen(false);
      setFeedbackReason('');
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to submit feedback. Please try again.',
        variant: 'error',
      });
    },
  });

  const submitThumbsDown = useCallback(
    (reason?: string) => {
      if (!('meta' in message) || message.meta == null || (message.meta as any).id == null) {
        return;
      }

      submitFeedbackMutation.mutate({
        params: {
          accountId: auth.account.id,
          sessionId: Number(session.id),
          logId: (message.meta as any).id,
        },
        body: {
          thumbs_down: true,
          ...(!isEmptyString(reason) ? { feedback_reason: reason } : {}),
        },
      });
      track('aiAssistant', 'thumbsDown', {
        'Feedback Text': reason ?? '',
        'Has Feedback': !isEmptyString(reason),
      });
    },
    [auth.account.id, message, session.id, submitFeedbackMutation, track],
  );

  const fileChips = useMemo(() => {
    if (message.role !== 'user') return [];

    return message.attachments.map(
      (attachment, index) =>
        ({
          id: `readonly-${index}`,
          name: attachment.display_name ?? 'File',
          mimeType: attachment.mime_type ?? 'application/octet-stream',
          source: attachment.content,
        } as FileData),
    );
  }, [message]);

  if (message.role === 'user') {
    const textContent = !isEmptyString(message.text) ? (
      <Markdown>{message.text}</Markdown>
    ) : (
      <Text as="p" size="s" color="subtle" fontStyle="italic">
        No message
      </Text>
    );

    return (
      <Box display="flex" flexDirection="column" gap="100">
        {fileChips.length > 0 ? (
          <Box display="flex" justifyContent="flex-end">
            <Attachments files={fileChips} session={session} variant="grid" />
          </Box>
        ) : null}
        <MessageContainer variant="user">
          <Box display="flex" flexDirection="column" gap="100">
            {textContent}
          </Box>
        </MessageContainer>
      </Box>
    );
  } else if (message.role === 'model' && message.type === 'text') {
    const sources = ('meta' in message && message.meta?.sources != null ? message.meta.sources : []) as Reference[];

    const { businessProfiles, otherSources } = sources.reduce<{
      businessProfiles: Reference[];
      otherSources: Reference[];
    }>(
      (accumulator, source) => {
        if (source?.type === 'business_profile') {
          accumulator.businessProfiles.push(source);
        } else {
          accumulator.otherSources.push(source);
        }
        return accumulator;
      },
      {
        businessProfiles: [],
        otherSources: [],
      },
    );

    const hasMetaId = 'meta' in message && message.meta?.id != null;
    const hasThumbsUp = 'meta' in message && message.meta?.thumbs_up === true;
    const hasThumbsDown = 'meta' in message && message.meta?.thumbs_down === true;
    const isThought = 'thought' in message && message.thought === true;
    const isV2 = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
    const showBusinessProfile = businessProfiles.length > 0 && isLastMessage && !isV2;
    const showReferences = otherSources.length > 0;

    if (isThought) {
      return <ThoughtMessage message={message} isStreaming={isStreaming} />;
    }

    return (
      <AnimateMessage>
        <MessageContainer variant="model">
          <Markdown isAnimating={isStreaming}>{message.text}</Markdown>
          {hasMetaId || showBusinessProfile || showReferences ? (
            <Box>
              <MessageActionsContainer isVisible={isLastMessage} alignBlock="center" alignInline="space-between">
                {hasMetaId ? (
                  <InlineStack gap="050" alignBlock="center">
                    <MessageAction
                      tooltip="Copy to clipboard"
                      onClick={async () => {
                        const success = await copyToClipboard(message.text);
                        if (success) {
                          snackbars.create({
                            message: 'Copied to clipboard',
                          });
                          track('aiAssistant', 'messageCopied', {});
                        }
                      }}
                      aria-label="Copy to clipboard"
                    >
                      <Icon name="copy-to-clipboard" size="xs" />
                    </MessageAction>
                    {!hasThumbsDown ? (
                      <MessageAction
                        tooltip="This was helpful"
                        variant={hasThumbsUp ? 'active' : 'default'}
                        onClick={
                          hasThumbsUp
                            ? undefined
                            : () => {
                                submitFeedbackMutation.mutate({
                                  params: {
                                    accountId: auth.account.id,
                                    sessionId: Number(session.id),
                                    logId: message.meta.id,
                                  },
                                  body: {
                                    thumbs_up: true,
                                  },
                                });
                                track('aiAssistant', 'thumbsUp', {});
                              }
                        }
                        aria-label="Thumbs up"
                      >
                        <Icon name="thumbs-up" size="xs" />
                      </MessageAction>
                    ) : null}
                    {!hasThumbsUp ? (
                      <Popover
                        placement="bottom-start"
                        withArrow={false}
                        isOpen={feedbackReasonOpen}
                        shouldTriggerOnClick={!hasThumbsDown}
                        onOpenChange={(open) => {
                          if (!open && !hasThumbsDown && !submitFeedbackMutation.isPending) {
                            submitThumbsDown(feedbackReason);
                          }
                          setFeedbackReasonOpen(open);
                          if (!open) {
                            setFeedbackReason('');
                          }
                        }}
                      >
                        {(context) => (
                          <MessageAction
                            ref={context.reference.setReference}
                            {...context.reference.getReferenceProps()}
                            tooltip={feedbackReasonOpen ? undefined : 'This was not helpful'}
                            variant={hasThumbsDown ? 'active' : 'default'}
                            aria-label="Thumbs down"
                          >
                            <Icon name="thumbs-down" size="xs" />
                          </MessageAction>
                        )}
                        <PopoverContent>
                          <Form
                            onSubmit={(event: FormEvent) => {
                              event.preventDefault();
                              submitThumbsDown(feedbackReason);
                            }}
                          >
                            <FormLayout>
                              <Text size="s" weight="semibold">
                                What went wrong?
                              </Text>
                              <TextArea
                                ariaLabel="Feedback reason"
                                placeholder="Tell us what could be improved..."
                                value={feedbackReason}
                                onInput={(event) => {
                                  setFeedbackReason(event.target.value);
                                }}
                                rows={3}
                                style={{ minWidth: '260px' }}
                              />
                              <InlineStack gap="100" alignInline="end">
                                <Button
                                  variant="subtle"
                                  size="sm"
                                  type="button"
                                  isDisabled={submitFeedbackMutation.isPending}
                                  onClick={() => {
                                    submitThumbsDown();
                                  }}
                                >
                                  Skip
                                </Button>
                                <Button
                                  variant="primary"
                                  type="submit"
                                  size="sm"
                                  isLoading={submitFeedbackMutation.isPending}
                                >
                                  Submit
                                </Button>
                              </InlineStack>
                            </FormLayout>
                          </Form>
                        </PopoverContent>
                      </Popover>
                    ) : null}
                  </InlineStack>
                ) : (
                  <div />
                )}
                {showBusinessProfile || showReferences ? (
                  <InlineStack gap="100" alignBlock="center">
                    {showBusinessProfile ? <BusinessProfileUsage references={businessProfiles} /> : null}
                    {showReferences ? <References references={otherSources} /> : null}
                  </InlineStack>
                ) : null}
              </MessageActionsContainer>
            </Box>
          ) : null}
        </MessageContainer>
      </AnimateMessage>
    );
  } else if (message.role === 'model' && message.type === 'tool_call') {
    return <ToolCalls message={message} session={session} />;
  } else if (message.role === 'model' && message.type === 'compaction') {
    return <CompactionMessage status={message.status} summary={message.summary} />;
  } else if (message.role === 'model' && message.type === 'error') {
    return (
      <AnimateMessage>
        <ErrorMessage error={message.error} errorDetails={message.error_details} />
      </AnimateMessage>
    );
  } else {
    // typescript hack to ensure all cases are handled
    const never: never = message;
    // eslint-disable-next-line no-console
    console.error('Unknown message type', never);
    return null;
  }
}

function ErrorMessage({ error, errorDetails }: { error: string; errorDetails?: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Banner variant="error" title="Error">
      <Stack gap="100">
        <Text>{error}</Text>
        {!isEmptyString(errorDetails) ? (
          <Stack gap="075">
            <PlainButton
              display="flex"
              alignItems="center"
              gap="025"
              onClick={() => setIsOpen((previous) => !previous)}
              aria-expanded={isOpen}
            >
              <Text size="xs" color="subtle">
                {isOpen ? 'Hide details' : 'Show details'}
              </Text>
              <motion.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon name="chevron-right" size="xs" color="subtle" />
              </motion.div>
            </PlainButton>
            {isOpen ? <CodeBlock code={errorDetails} language="text" mode="dark" /> : null}
          </Stack>
        ) : null}
      </Stack>
    </Banner>
  );
}

function CompactionMessage({ status, summary }: { status: 'compacting' | 'complete'; summary?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const hasSummary = summary != null && summary !== '';
  const isExpandable = status === 'complete' && hasSummary;

  useEffect(() => {
    if (status !== 'compacting') {
      setProgress(100);
      return;
    }

    // Animate progress with diminishing increments (never reaches 100 until complete)
    const interval = setInterval(() => {
      setProgress((previous) => {
        const remaining = 90 - previous;
        return previous + remaining * 0.015;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [status]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column" gap="050" py="100">
        {status === 'compacting' ? (
          <>
            <Text size="s" color="subtle" fontStyle="italic">
              Compacting our conversation so we can keep chatting…
            </Text>
            <Box display="flex" alignItems="center" gap="100">
              <Box
                flexGrow="1"
                style={{
                  height: '4px',
                  borderRadius: themeVars.radius['full'],
                  backgroundColor: themeVars.color['surface-active'],
                  overflow: 'hidden',
                }}
              >
                <Box
                  style={{
                    height: '100%',
                    width: `${Math.round(progress)}%`,
                    borderRadius: themeVars.radius['full'],
                    backgroundColor: themeVars.color['surface-bold'],
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Text size="xs" color="subtle">
                {Math.round(progress)}%
              </Text>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap="050"
            cursor={isExpandable ? 'pointer' : undefined}
            onClick={isExpandable ? () => setIsOpen((previous) => !previous) : undefined}
          >
            {isExpandable ? (
              isOpen ? (
                <Icon name="chevron-down" size="sm" />
              ) : (
                <Icon name="chevron-right" size="sm" />
              )
            ) : null}
            <Icon color="success" name="check-circle" size="sm" />
            <Text size="xs" color="subtle">
              Conversation compacted
            </Text>
          </Box>
        )}
        {isOpen && hasSummary ? (
          <Box px="100" pt="050">
            <Markdown>{summary}</Markdown>
          </Box>
        ) : null}
      </Box>
    </AnimateMessage>
  );
}

function ThoughtMessage({
  message,
  isStreaming,
}: {
  message: CopilotStatefulMessage & { type: 'text'; thought?: boolean };
  isStreaming?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const wasStreamingRef = useRef(false);

  const durationSeconds = 'duration' in message && typeof message.duration === 'number' ? message.duration : null;

  const formattedDuration = (() => {
    if (durationSeconds == null) return null;

    if (durationSeconds < 60) {
      const unit = durationSeconds === 1 ? 'second' : 'seconds';
      return `${durationSeconds} ${unit}`;
    } else {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      if (seconds === 0) {
        const unit = minutes === 1 ? 'minute' : 'minutes';
        return `${minutes} ${unit}`;
      }
      const minuteUnit = minutes === 1 ? 'minute' : 'minutes';
      const secondUnit = seconds === 1 ? 'second' : 'seconds';
      return `${minutes} ${minuteUnit} ${seconds} ${secondUnit}`;
    }
  })();

  // Auto-open when streaming starts, auto-close when streaming ends
  useEffect(() => {
    if (isStreaming === true) {
      wasStreamingRef.current = true;
      setIsOpen(true);
    } else if (wasStreamingRef.current) {
      wasStreamingRef.current = false;
      setIsOpen(false);
    }
  }, [isStreaming]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          alignItems="center"
          gap="075"
          py="075"
          cursor="pointer"
          onClick={() => setIsOpen((previous) => !previous)}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((previous) => !previous);
            }
          }}
        >
          <Text as={formattedDuration == null ? ShimmerText : 'span'} color="subtle" size="s">
            {formattedDuration != null ? `Thought for ${formattedDuration}` : 'Thinking\u2026'}
          </Text>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="chevron-down" size="xs" color="subtle" />
          </motion.div>
        </Box>
        <motion.div
          initial={false}
          animate={{
            height: isOpen ? 'auto' : 0,
            opacity: isOpen ? 1 : 0,
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <Box py="075">
            <Markdown isAnimating={isStreaming} color="subtle" size="s">
              {message.text}
            </Markdown>
          </Box>
        </motion.div>
      </Box>
    </AnimateMessage>
  );
}

function toolCallStepStatus(status: CopilotToolCallStatus): StepStatus {
  if (status === 'executing') return 'active';
  if (status === 'error') return 'error';
  return 'complete';
}

function combinedStatus(toolCalls: CopilotStatefulToolCall[]): CopilotToolCallStatus {
  if (toolCalls.some((toolCall) => toolCall.status === 'executing')) return 'executing';
  if (toolCalls.some((toolCall) => toolCall.status === 'error')) return 'error';
  return 'success';
}

function buildHeaderLabel(toolCalls: CopilotStatefulToolCall[]): string {
  if (toolCalls.length === 0) return '';

  // Prefer the first executing tool for the header, fall back to last success
  const representative =
    toolCalls.find((toolCall) => toolCall.status === 'executing') ?? toolCalls[toolCalls.length - 1];

  const label = getToolCallHeaderLabel(representative);
  const suffix = toolCalls.length > 1 ? ` (+${toolCalls.length - 1} more)` : '';
  return label + suffix;
}

/**
 * For approved bash tool calls, the approval step has the command but only an
 * `{ status: "approved" }` response. The actual execution result (stdout/stderr)
 * lives on a sibling tool call. This helper finds it by matching on the same
 * command string, falling back to any bash sibling with output.
 */
function getBashResponse(
  toolCall: CopilotStatefulToolCall,
): { stdout?: string; stderr?: string; exit_code?: number } | null {
  const result = toolCall.response?.result;
  if (result != null && typeof result === 'object' && ('stdout' in result || 'stderr' in result)) {
    return result;
  }
  return null;
}

function ToolCallStep({ toolCall }: { toolCall: CopilotStatefulToolCall }) {
  const references = toolCall.response?.result?.references as Array<{ title?: string; url?: string }> | undefined;
  const uniqueReferences = dedupeReferences(references);
  const isFileOp = toolCall.name === 'read' || toolCall.name === 'write';
  const rawPath = typeof toolCall.args?.path === 'string' ? toolCall.args.path : undefined;
  const path = rawPath != null ? extractFilename(rawPath) : undefined;
  const errorMessage =
    toolCall.status === 'error'
      ? typeof toolCall.response?.error === 'string'
        ? toolCall.response.error
        : 'An error occurred'
      : undefined;

  return (
    <ChainOfThoughtStep
      label={getToolCallLabel(toolCall)}
      status={toolCallStepStatus(toolCall.status)}
      description={errorMessage}
      icon={toolCall.name === 'search_docs' ? 'search' : undefined}
      expandable={toolCall.name === 'bash' && typeof toolCall.args?.command === 'string'}
      badge={isFileOp && path != null ? <ChainOfThoughtSource>{path}</ChainOfThoughtSource> : undefined}
    >
      {toolCall.name === 'bash' && typeof toolCall.args?.command === 'string' ? (
        <BashDetail command={toolCall.args.command} response={getBashResponse(toolCall)} />
      ) : null}
      {toolCall.name === 'search_docs' && uniqueReferences.length > 0 ? (
        <ChainOfThoughtSources>
          {uniqueReferences.map((reference) => (
            <ChainOfThoughtSource key={reference.url ?? reference.title} href={reference.url}>
              {reference.title}
            </ChainOfThoughtSource>
          ))}
        </ChainOfThoughtSources>
      ) : null}
    </ChainOfThoughtStep>
  );
}

function StandardToolCallSegment({ toolCalls }: { toolCalls: CopilotStatefulToolCall[] }) {
  const status = combinedStatus(toolCalls);
  const headerLabel = buildHeaderLabel(toolCalls);

  return (
    <AnimateMessage>
      <ChainOfThought isStreaming={status === 'executing'}>
        <ChainOfThoughtHeader>{headerLabel}</ChainOfThoughtHeader>
        <ChainOfThoughtContent>
          {toolCalls.map((toolCall, index) => (
            <ToolCallStep key={index} toolCall={toolCall} />
          ))}
        </ChainOfThoughtContent>
      </ChainOfThought>
    </AnimateMessage>
  );
}

/**
 * Fallback UI for a crashed tool render. Extracted into its own component
 * so it can use `useState` to guard the Continue button against double-clicks.
 */
function ToolCallErrorFallback({ toolCall, error }: { toolCall: CopilotStatefulToolCall; error: Error }) {
  const [continued, setContinued] = useState(false);
  const canContinue = toolCall.status === 'executing' && toolCall.respond != null && !continued;

  return (
    <ChainOfThought>
      <ChainOfThoughtStep
        label={toolCall.name}
        status="error"
        description={`Something went wrong rendering this tool: ${error.message}`}
      />
      {canContinue ? (
        <Box px="100" pb="100">
          <Button
            variant="subtle"
            size="sm"
            onClick={() => {
              setContinued(true);
              toolCall.respond?.('The tool UI crashed and could not be displayed. Please continue without this tool.');
            }}
          >
            Continue
          </Button>
        </Box>
      ) : null}
    </ChainOfThought>
  );
}

/**
 * Error boundary for custom tool renders. When a render crash occurs,
 * shows a degraded ChainOfThought step with a Continue button for
 * human-in-the-loop tools so the conversation isn't permanently stuck.
 */
function ToolCallErrorBoundary({
  toolCall,
  children,
}: {
  toolCall: CopilotStatefulToolCall;
  children: React.ReactNode;
}) {
  return (
    <AppErrorBoundary fallbackRender={({ error }) => <ToolCallErrorFallback toolCall={toolCall} error={error} />}>
      {children}
    </AppErrorBoundary>
  );
}

function ToolCalls({
  message,
  session,
}: {
  message: CopilotStatefulMessage & { type: 'tool_call' };
  session: CopilotSession;
}) {
  const tools = useCopilotAvailableTools(session);
  const ApprovalComponent = useCopilotApproval();

  const resolveToolRender = (toolCall: (typeof message.tool_calls)[0]) => {
    // Only use the approval renderer while the approval is still pending (executing).
    // Completed approvals fall through to standard rendering.
    if (toolCall.needs_approval === true && toolCall.status === 'executing' && ApprovalComponent != null) {
      return (props: { session: CopilotSession } & (typeof message.tool_calls)[0]) => {
        const sendApprovalResponse = (decision: { status: 'approved' | 'rejected'; reason?: string }) => {
          props.session.sendMessage([
            decision.status === 'approved'
              ? {
                  role: 'user',
                  type: 'tool_response',
                  tool_responses: [
                    {
                      tool_call_id: props.id,
                      name: props.name,
                      response: { result: decision, error: null },
                      finished: true,
                    },
                  ],
                  timestamp: Math.floor(Date.now() / 1000),
                }
              : buildApprovalDenialMessage([{ id: props.id, name: props.name }], decision.reason),
          ]);
        };

        return (
          <ApprovalComponent
            toolCall={props}
            session={props.session}
            onApprove={() => sendApprovalResponse({ status: 'approved' })}
            onDeny={(reason) => sendApprovalResponse({ status: 'rejected', reason })}
          />
        );
      };
    }

    return tools[toolCall.name]?.render;
  };
  // Build ordered segments that preserve the original tool call order.
  // Consecutive non-custom tools are grouped into a single collapsible block;
  // custom-rendered tools (approvals, etc.) are rendered individually in place.
  type CustomSegment = { type: 'custom'; toolCall: (typeof message.tool_calls)[0] };
  type StandardSegment = { type: 'standard'; toolCalls: (typeof message.tool_calls)[0][] };
  type Segment = CustomSegment | StandardSegment;

  const segments = message.tool_calls.reduce<Segment[]>((accumulator, toolCall) => {
    // Only use custom approval rendering while the approval is still pending.
    // Once completed (approved/rejected), render as a standard collapsible tool call.
    const isActiveApproval =
      toolCall.needs_approval === true && toolCall.status === 'executing' && ApprovalComponent != null;
    const hasCustomRender = isActiveApproval || resolveToolRender(toolCall) != null;

    if (hasCustomRender) {
      return [...accumulator, { type: 'custom', toolCall }];
    }

    const lastSegment = accumulator[accumulator.length - 1];
    if (lastSegment != null && lastSegment.type === 'standard') {
      return [...accumulator.slice(0, -1), { ...lastSegment, toolCalls: [...lastSegment.toolCalls, toolCall] }];
    }

    return [...accumulator, { type: 'standard', toolCalls: [toolCall] }];
  }, []);

  return (
    <>
      {segments.map((segment, segmentIndex) => {
        if (segment.type === 'custom') {
          const render = resolveToolRender(segment.toolCall);

          if (render == null) {
            return (
              <AnimateMessage key={segmentIndex}>
                <ChainOfThought>
                  <ChainOfThoughtStep label={segment.toolCall.name} status="error" />
                </ChainOfThought>
              </AnimateMessage>
            );
          }

          return (
            <AnimateMessage key={segmentIndex}>
              <ToolCallErrorBoundary toolCall={segment.toolCall}>
                <Box>{render({ ...segment.toolCall, session })}</Box>
              </ToolCallErrorBoundary>
            </AnimateMessage>
          );
        }

        return <StandardToolCallSegment key={segmentIndex} toolCalls={segment.toolCalls} />;
      })}
    </>
  );
}

const SUGGESTIONS_ANIMATION_DURATION = 0.24;
const SUGGESTIONS_ANIMATION_EASE: [number, number, number, number] = [0.2, 0, 0.38, 0.9];
const SUGGESTIONS_TRANSITION = {
  duration: SUGGESTIONS_ANIMATION_DURATION,
  ease: SUGGESTIONS_ANIMATION_EASE,
} as const;

function NextMessageSuggestions({
  state,
  onSuggestionClick,
  shouldIncludeHumanSupport = false,
}: {
  state: SuggestionState;
  onSuggestionClick: (message: string, index: number) => void;
  shouldIncludeHumanSupport?: boolean;
}) {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [suggestionsHidden, setSuggestionsHidden] = useLocalStorage('copilot-suggestions-hidden', false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const { track } = useAnalytics();
  const shouldReduceMotion = useReducedMotion();

  const isContentVisible = !suggestionsHidden || isCollapsing;
  const isContentInert = isCollapsing || isExpanding;

  const handleToggleSuggestions = useCallback(() => {
    if (suggestionsHidden) {
      setSuggestionsHidden(false);
      setIsExpanding(true);
      track('aiAssistant', 'suggestionsShown', {});
    } else {
      track('aiAssistant', 'suggestionsHidden', {});
      if (shouldReduceMotion === true) {
        setSuggestionsHidden(true);
      } else {
        setIsCollapsing(true);
      }
    }
  }, [suggestionsHidden, shouldReduceMotion, setSuggestionsHidden, track]);

  const handleCollapseComplete = useCallback(() => {
    setSuggestionsHidden(true);
    setIsCollapsing(false);
  }, [setSuggestionsHidden]);

  const handleExpandComplete = useCallback(() => {
    setIsExpanding(false);
  }, []);

  if (state.isVisible === false || state.isLoading === true) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: shouldReduceMotion === true ? 0 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={shouldReduceMotion === true ? { duration: 0 } : SUGGESTIONS_TRANSITION}
    >
      <Box flexShrink="0">
        <Divider />
        <Box display="flex" alignItems="center" justifyContent="space-between" px="150" pt="100">
          <Text size="xs">
            <Link
              icon={suggestionsHidden ? 'view-on' : 'view-off'}
              isUnderlined={false}
              variant="secondary"
              onClick={handleToggleSuggestions}
              aria-expanded={!suggestionsHidden}
            >
              {suggestionsHidden ? 'Show suggestions' : 'Hide suggestions'}
            </Link>
          </Text>
          <Box
            {...(suggestionsHidden || isContentInert ? { inert: true, style: { visibility: 'hidden' as const } } : {})}
          >
            <Button
              isIconOnly={true}
              icon="chevron-left"
              variant="subtle"
              size="sm"
              aria-label="Scroll suggestions left"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft - scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
            <Button
              isIconOnly={true}
              icon="chevron-right"
              variant="subtle"
              size="sm"
              aria-label="Scroll suggestions right"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft + scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
          </Box>
        </Box>
        {isContentVisible ? (
          <motion.div
            initial={isExpanding ? { gridTemplateRows: '0fr' } : false}
            animate={{ gridTemplateRows: isCollapsing ? '0fr' : '1fr' }}
            transition={shouldReduceMotion === true ? { duration: 0 } : SUGGESTIONS_TRANSITION}
            className={chatStyles.suggestionsCollapseGrid}
            onAnimationComplete={() => {
              if (isCollapsing) {
                handleCollapseComplete();
              }
              if (isExpanding) {
                handleExpandComplete();
              }
            }}
            {...(isContentInert ? { inert: true } : {})}
          >
            <div className={chatStyles.suggestionsCollapseContent}>
              <Scrollable
                overflow="x"
                withEdgeIndicators
                display="flex"
                gap="100"
                ref={scrollableRef}
                p="100"
                className={chatStyles.hiddenScrollbar}
              >
                <Suggestions
                  suggestions={state.suggestions}
                  onSuggestionClick={onSuggestionClick}
                  shouldShowPlaceholder={false}
                  shouldIncludeHumanSupport={shouldIncludeHumanSupport}
                />
              </Scrollable>
            </div>
          </motion.div>
        ) : null}
      </Box>
    </motion.div>
  );
}

/**
 * Builds a tool_response message that rejects one or more pending approval tool calls.
 */
function buildApprovalDenialMessage(approvals: Array<{ id: string; name: string }>, reason?: string): CopilotMessage {
  return {
    role: 'user',
    type: 'tool_response',
    tool_responses: approvals.map((approval) => ({
      tool_call_id: approval.id,
      name: approval.name,
      response: { result: { status: 'rejected' as const, reason }, error: null },
      finished: true,
    })),
    timestamp: Math.floor(Date.now() / 1000),
  };
}

function Input({
  session,
  input,
  setInput,
  onSubmit,
  placeholder: customPlaceholder,
  wrapperClassName: customWrapperClassName,
  resetSession,
}: {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  session: CopilotSession;
  onSubmit?: () => void;
  placeholder?: string;
  wrapperClassName?: string;
  resetSession?: () => CopilotSession;
}) {
  const lastInput = useRef<string | null>(null);
  const lastFiles = useRef<CopilotUploadFile[]>([]);
  const fileUploadAreaRef = useRef<FileUploadAreaRef>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const status = useCopilotStatus(session);
  const isLoading =
    resetSession == null && (status === 'sending' || status === 'receiving' || status === 'reconnecting');
  const queuedMessages = useCopilotQueuedMessages(session);
  // Filter to only user-visible text messages — tool_response messages (e.g. from
  // auto-approve) are system messages that shouldn't appear in the queued messages UI.
  const visibleQueuedMessages = useMemo(
    () => queuedMessages.filter((m): m is CopilotMessage & { text: string } => m.type === 'text' && 'text' in m),
    [queuedMessages],
  );
  const networkStatus = useNetworkStatus();

  // File upload functionality
  const fileUpload = useCopilotFileUpload(session);

  // Handle files from both drag drop and file input
  const handleFiles = useCallback(
    (files: File[]) => {
      const result = fileUpload.addFiles(files);

      const messages: string[] = [];

      if (result.errors.length > 0) {
        messages.push(...result.errors.map((error) => `${error.name}: ${error.error}`));
      }

      if (messages.length > 0) {
        setUploadError(messages.join('\n'));
      }

      // Immediately start uploading the files with retry for transient network errors
      if (result.addedCount > 0) {
        const pendingFiles = session.getPendingUploadFiles();
        uploadFilesWithRetry(pendingFiles, fileUpload.uploadFile, fileUpload.removeFile)
          .then((uploadResult) => {
            // Handle failed uploads - accumulate with validation errors
            if (uploadResult.failed.length > 0) {
              const failedMessages = uploadResult.failed.map((f) => `${f.name}: ${f.error}`);

              // Append to existing validation errors if any
              setUploadError((previousError) => {
                if (!isEmptyString(previousError)) {
                  return previousError + '\n' + failedMessages.join('\n');
                }
                return failedMessages.join('\n');
              });

              // Report to Sentry
              for (const f of uploadResult.failed) {
                sentryCaptureException(new Error(`File upload failed for ${f.name}: ${f.error}`));
              }
            } else if (uploadResult.successful.length > 0 && messages.length === 0) {
              // Clear error if all uploads succeeded and there were no validation errors
              setUploadError(null);
            }

            if (uploadResult.successful.length > 0) {
              const fileTypes = uploadResult.successful.map((f) => {
                const extension = f.name.split('.').pop()?.toLowerCase() ?? 'unknown';
                return extension;
              });
              track('aiAssistant', 'fileUploaded', {
                Count: uploadResult.successful.length,
                'File Types': fileTypes,
              });
            }
          })
          .catch((error) => {
            // This catch should rarely be hit since uploadFilesWithRetry uses Promise.allSettled
            sentryCaptureException(error);
            setUploadError(error instanceof Error ? error.message : 'Failed to upload files');
          });
      }
    },
    [fileUpload, session, track],
  );

  const fileChips = useMemo(
    () =>
      fileUpload.files.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
        source: file.file,
        status: file.status,
      })),
    [fileUpload.files],
  );

  async function submit(input: string, files: CopilotUploadFile[] = []) {
    setError(null);
    try {
      // Create message payload
      const messages: CopilotMessage[] = [];

      // Add file messages for uploaded files
      const uploadedFiles = files.filter((f) => f.status === 'uploaded' && f.uploadedUrl != null);
      for (const file of uploadedFiles) {
        messages.push({
          role: 'user',
          type: 'content',
          content: file.uploadedUrl ?? '',
          mime_type: file.mimeType,
          display_name: file.name,
        });
      }

      // Add text message if there's input
      if (!isEmptyString(input)) {
        messages.push({
          role: 'user',
          type: 'text',
          text: input,
        });
      }

      // Clear input and files indicating that the user has sent the message
      setInput('');
      fileUpload.clearFiles();

      // If we have messages to send, send them
      if (messages.length > 0) {
        const targetSession = resetSession?.() ?? session;
        onSubmit?.();
        // Read messages imperatively (not via hook subscription) to avoid
        // re-rendering the input component on every streaming message update.
        const currentMessages = targetSession.__statefulMessages.get();
        const userTurnCount = currentMessages.filter((m) => m.role === 'user').length;
        await targetSession.sendMessage(messages);
        track('aiAssistant', 'messageSent', {
          Message: input,
          'Has Files': uploadedFiles.length > 0,
          'File Count': uploadedFiles.length,
          'Turn Number': userTurnCount + 1,
          'Is First Message': userTurnCount === 0,
          'Conversation ID': targetSession.id,
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }

  const handleUploadButtonClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (fileUploadAreaRef.current != null) {
      fileUploadAreaRef.current.triggerFileInput();
    }
  }, []);

  const handleStop = useCallback(async () => {
    track('aiAssistant', 'stopClicked', {});
    const { messages, error } = await session.stop();

    // Always restore queued messages to the input box, even if the backend
    // cancellation failed — the local stream is already aborted either way.
    const texts = messages
      .filter((m): m is CopilotMessage & { text: string } => m.type === 'text' && 'text' in m)
      .map((m) => m.text);
    if (texts.length > 0) {
      // Use a functional updater so we always prepend onto the latest input
      // value — not the one captured when this callback was created. The
      // component becomes interactive as soon as the stream aborts (before
      // stopExchange resolves), so the user may have typed in the window
      // between abort and the await returning.
      const queued = texts.join('\n\n');
      setInput((previous) => [queued, previous].filter((t) => t.trim() !== '').join('\n\n'));
    }

    if (error != null) {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to stop the response. Please try again.',
        variant: 'error',
      });
    }
  }, [session, setInput, snackbars, track]);

  const isV2 = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const { isAllApproved, setIsAllApproved } = useApprovedResources();
  const enableWebSearch = useCopilotWebSearch(session);
  const workspace = useCurrentWorkspace();
  const safeNavigate = useSafeNavigate();
  const showFeedbackModal = useCustomerFeedback();
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];

  const hasContent = useMemo<boolean>(
    () => !isEmptyString(input) || fileUpload.files.length > 0,
    [input, fileUpload.files],
  );
  const hasUploadingFiles = useMemo<boolean>(
    () => fileUpload.files.some((f) => f.status === 'uploading' || f.status === 'pending'),
    [fileUpload.files],
  );
  const canSubmit = hasContent && !hasUploadingFiles;

  return (
    <>
      {uploadError != null ? (
        <Banner
          title="Upload Error"
          variant="error"
          mb="100"
          shouldShowDismissButton={true}
          onClose={() => setUploadError(null)}
        >
          <Box whiteSpace="pre-line">{uploadError}</Box>
        </Banner>
      ) : null}
      {error != null ? (
        <Banner
          title="Error"
          variant="error"
          mb="100"
          action={{
            label: 'Retry',
            onClick: () => {
              if (lastInput.current != null || lastFiles.current.length > 0) {
                submit(lastInput.current ?? '', lastFiles.current);
              }
            },
          }}
          shouldShowDismissButton={true}
          onClose={() => setError(null)}
        >
          {error}
        </Banner>
      ) : null}

      {status === 'reconnecting' ? (
        <Banner title="Reconnecting" variant="warning" mb="100">
          The connection was interrupted. Attempting to recover...
        </Banner>
      ) : null}

      {isLoading && visibleQueuedMessages.length > 0 ? (
        <Box
          mb="100"
          p="100"
          display="flex"
          flexDirection="column"
          gap="075"
          className={chatStyles.queuedMessagesContainer}
        >
          <InlineStack
            alignBlock="center"
            alignInline="space-between"
            pb="050"
            className={chatStyles.queuedMessagesHeader}
          >
            <InlineStack alignBlock="center" gap="050">
              <Icon name="time-window" size="xs" color="subtle" />
              <Text size="xs" color="subtle" weight="medium">
                Queued messages
              </Text>
            </InlineStack>
            <InlineStack alignBlock="center" gap="050">
              <Text size="xs" color="subtle">
                Press
              </Text>
              <KeyboardShortcut hotkey="ArrowUp" />
              <Text size="xs" color="subtle">
                to edit
              </Text>
            </InlineStack>
          </InlineStack>
          <QueuedMessage messages={visibleQueuedMessages} />
        </Box>
      ) : null}
      <FileUploadArea
        ref={fileUploadAreaRef}
        onFilesSelected={handleFiles}
        disabled={networkStatus === 'offline'}
        style={{ position: 'relative' }}
        acceptedMimeTypes={Object.keys(SUPPORTED_FILE_TYPES)}
      >
        <Form
          onSubmit={async (event) => {
            event.preventDefault();

            if (!canSubmit) {
              return;
            }

            if (networkStatus === 'offline') {
              return;
            }

            // Only update retry refs if this is a direct send (not queued).
            // If the session is busy, sendMessage will queue — don't overwrite
            // the retry refs for the in-flight message.
            if (status !== 'sending' && status !== 'receiving') {
              lastInput.current = input;
              lastFiles.current = Array.from(fileUpload.files);
            }
            await submit(input, Array.from(fileUpload.files));
          }}
          position="relative"
          autoLoading={false}
        >
          <TextArea
            value={input}
            onInput={(event) => setInput(event.target.value)}
            onKeyDown={(event: KeyboardEvent) => {
              // Arrow up at the top of the input: pull all queued messages back into the editor
              if (event.key === 'ArrowUp' && visibleQueuedMessages.length > 0) {
                const textarea = event.target as HTMLTextAreaElement;
                const isAtTop = textarea.selectionStart === 0 && textarea.selectionEnd === 0;
                if (isAtTop) {
                  event.preventDefault();
                  const cleared = session.clearQueuedMessages();
                  const texts = cleared
                    .filter((m): m is CopilotMessage & { text: string } => 'text' in m && m.type === 'text')
                    .map((m) => m.text);
                  const restoredText = texts.join('\n');
                  const combined = [restoredText, input].filter((t) => t.trim() !== '').join('\n\n');
                  setInput(combined);
                }
              }
            }}
            onPaste={(event) => {
              // Extract image files from clipboard
              const items = Array.from(event.clipboardData?.items ?? []);
              const imageFiles = items
                .filter((item: DataTransferItem) => item.type.startsWith('image/'))
                .map((item: DataTransferItem) => item.getAsFile())
                .filter((file): file is File => file != null);

              if (imageFiles.length > 0) {
                event.preventDefault();
                handleFiles(imageFiles);
              }
            }}
            id="copilot-chat-input"
            placeholder={
              fileChips.length > 0 ? 'Add a message (optional)' : customPlaceholder ?? 'How can I help you today?'
            }
            shouldSubmitOnEnter={true}
            ariaLabel={isV2 === true ? 'Send message to Agent' : 'Send message to Assistant'}
            resize="none"
            minRows={1}
            maxRows={6}
            unsafe_textareaWrapperClassName={[
              styles.chatTextareaWrapper,
              fileChips.length > 0 ? styles.chatTextareaWrapperWithFiles : '',
              customWrapperClassName ?? '',
            ]
              .filter(Boolean)
              .join(' ')}
            unsafe_inputClassName={styles.chatTextareaInput}
          />

          {/* File chips positioned absolutely */}
          {fileChips.length > 0 ? (
            <Box
              style={{
                position: 'absolute',
                top: themeVars.space['100'],
                left: themeVars.space['100'],
                right: themeVars.space['600'],
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              <Box style={{ pointerEvents: 'auto' }}>
                <Attachments
                  files={fileChips}
                  variant="inline"
                  readOnly={false}
                  onRemove={fileUpload.removeFile}
                  session={session}
                />
              </Box>
            </Box>
          ) : null}

          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
            style={{
              position: 'absolute',
              bottom: themeVars.space['100'],
              left: themeVars.space['100'],
              right: themeVars.space['100'],
              pointerEvents: 'none',
            }}
          >
            <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
              {isV2 ? (
                <>
                  <Button
                    icon="paperclip"
                    isIconOnly={true}
                    variant="tertiary"
                    type="button"
                    size="sm"
                    style={{ border: 'none' }}
                    isDisabled={networkStatus === 'offline'}
                    onClick={handleUploadButtonClick}
                  />
                  <DropdownMenu>
                    <DropdownMenuTriggerButton
                      isIconOnly
                      icon="properties"
                      variant="tertiary"
                      size="sm"
                      style={{ border: 'none' }}
                    />
                    <DropdownMenuContent>
                      <DropdownMenuGroup label="Capabilities">
                        <DropdownMenuCheckboxItem
                          isChecked={isAllApproved}
                          onCheckedChange={(checked) => {
                            setIsAllApproved(checked);
                            track('aiAssistant', 'applyChangesToggled', { Enabled: checked });
                          }}
                          shouldCloseOnClick={false}
                        >
                          Apply changes automatically
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          isChecked={enableWebSearch}
                          onCheckedChange={(checked) => {
                            session.setWebSearchEnabled(checked);
                            track('aiAssistant', 'webSearchToggled', { Enabled: checked });
                          }}
                          shouldCloseOnClick={false}
                        >
                          Enable web search
                        </DropdownMenuCheckboxItem>
                      </DropdownMenuGroup>
                      <DropdownMenuGroup label="Settings">
                        {workspace != null ? (
                          <DropdownMenuItem
                            isDisabled={lastMatch?.id === 'workspace_settings_ai_business_profile'}
                            onClick={() => {
                              track('aiAssistant', 'editBusinessProfileClicked', { 'Click Location': 'menu' });
                              safeNavigate(
                                generatePath('/workspaces/:workspaceId/settings/ai-business-profile', {
                                  workspaceId: workspace.id,
                                }),
                              );
                            }}
                          >
                            Add context
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          onClick={() => {
                            track('aiAssistant', 'feedbackLinkClicked', {});
                            void showFeedbackModal({
                              feature: isV2 === true ? 'AI Agent' : 'AI Assistant',
                              onSubmit: (values) => {
                                track('aiAssistant', 'feedbackFormSubmitted', {
                                  Feedback: values.feedback,
                                });
                              },
                            });
                          }}
                        >
                          Give feedback
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : null}
              <TokenUsage session={session} />
            </Box>
            <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
              {!isV2 ? (
                <Button
                  icon="paperclip"
                  isIconOnly={true}
                  variant="tertiary"
                  type="button"
                  size="sm"
                  style={{ border: 'none' }}
                  isDisabled={networkStatus === 'offline'}
                  onClick={handleUploadButtonClick}
                />
              ) : null}
              {isLoading ? (
                <Button icon="stop" isIconOnly={true} variant="primary" type="button" size="sm" onClick={handleStop} />
              ) : (
                <Button
                  icon="chevron-up"
                  isIconOnly={true}
                  variant="primary"
                  type="submit"
                  size="sm"
                  isDisabled={networkStatus === 'offline' || !canSubmit}
                />
              )}
            </Box>
          </Box>
        </Form>
      </FileUploadArea>
      <FooterStatment input={input} />
    </>
  );
}

/**
 * Extracts the latest token usage info from the session's raw messages.
 * Scans backwards through messages to find the most recent meta message
 * that includes token usage data.
 */
function useTokenUsage(session: CopilotSession) {
  const messages = useStore(session.__messages);

  return useMemo(() => {
    // Scan backwards for the most recent meta message with token usage
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message.role === 'model' && message.type === 'meta' && 'meta' in message) {
        const meta = (message as CopilotModelMetaMessage).meta;
        if (meta?.prompt_tokens_used != null && meta.prompt_tokens_used > 0) {
          return {
            promptTokensUsed: meta.prompt_tokens_used,
            contextWindowSize: meta.context_window_size ?? 0,
          };
        }
      }
    }
    return null;
  }, [messages]);
}

function TokenUsage({ session }: { session: CopilotSession }) {
  const usage = useTokenUsage(session);

  if (usage == null) {
    return <Box />;
  }

  const { promptTokensUsed, contextWindowSize } = usage;
  const percentage = contextWindowSize > 0 ? Math.round((promptTokensUsed / contextWindowSize) * 100) : null;

  if (percentage == null || percentage < 80) {
    return <Box />;
  }

  const tooltipContent = `This conversation is using ${percentage}% of the available memory. As it grows, older details may be dropped to fit new messages.`;

  return (
    <Tooltip content={tooltipContent} isCustom={true}>
      {(context) => (
        <Paragraph
          text="product-pxs-secondary"
          color="subtle"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          {percentage}% memory used
        </Paragraph>
      )}
    </Tooltip>
  );
}

function FooterStatment({ input }: { input: string }) {
  const networkStatus = useNetworkStatus();

  return (
    <Paragraph text="product-pxs-secondary" pt="050" color="subtle">
      {networkStatus === 'offline' ? (
        <>{input.length > 0 ? <>You can't send messages when offline</> : <>You are currently offline</>}</>
      ) : input.length > 0 ? (
        <>
          <Strong weight="medium">Shift + Return</Strong> to add a new line
        </>
      ) : (
        <>Agent is AI and can make mistakes. Please double-check responses.</>
      )}
    </Paragraph>
  );
}

function QueuedMessage({ messages }: { messages: (CopilotMessage & { text: string })[] }) {
  if (messages.length === 0) {
    return null;
  }

  return (
    <Stack gap="050">
      {messages.map((m, index) => (
        <Text key={index} size="xs" color="subtle">
          <Truncated>{m.text}</Truncated>
        </Text>
      ))}
    </Stack>
  );
}

const CYCLING_STATUS_WORDS = [
  'Thinking…',
  'Working…',
  'Analyzing…',
  'Researching…',
  'Considering…',
  'Processing…',
  'Exploring…',
  'Reasoning…',
  'Evaluating…',
  'Pondering…',
  'Cooking…',
  'Brewing something up…',
  'Scribbling on a napkin…',
  'Connecting dots…',
  'Doing some napkin calculations…',
  'Questioning everything…',
  'Becoming sentient (just kidding)…',
];

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const index_ = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[index_]] = [shuffled[index_], shuffled[index]];
  }
  return shuffled;
}

function CyclingStatusText() {
  const shouldReduceMotion = useReducedMotion();
  const [words] = useState(() => shuffleArray(CYCLING_STATUS_WORDS));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion === true) return;
    const interval = setInterval(() => {
      setIndex((previous) => (previous + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [shouldReduceMotion, words.length]);

  if (shouldReduceMotion === true) {
    return (
      <Box display="flex" alignItems="center" gap="050">
        <ThinkingIcon />
        <ShimmerText variant="gradient" size="s">
          Working…
        </ShimmerText>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap="050">
      <ThinkingIcon />
      <Box position="relative" style={{ overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={words[index]}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.25 }}
          >
            <ShimmerText variant="gradient" size="s">
              {words[index]}
            </ShimmerText>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
}

function AnimateMessage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
      {children}
    </motion.div>
  );
}

function ChatSkeleton() {
  return (
    <Box display="flex" flexDirection="column" gap="300" py="200" px="100">
      {/* User message skeleton — right-aligned bubble */}
      <Box display="flex" justifyContent="flex-end">
        <SkeletonShape style={{ width: '55%', height: 40, borderRadius: 18 }} />
      </Box>
      {/* Model response skeleton — left-aligned lines */}
      <Box display="flex" flexDirection="column" gap="100">
        <SkeletonText text="product-p" lines={3} />
      </Box>
      {/* Second user message */}
      <Box display="flex" justifyContent="flex-end">
        <SkeletonShape style={{ width: '40%', height: 40, borderRadius: 18 }} />
      </Box>
      {/* Second model response */}
      <Box display="flex" flexDirection="column" gap="100">
        <SkeletonText text="product-p" lines={2} />
      </Box>
    </Box>
  );
}
